import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cashReservesService from '../../services/cashReservesService';
import EditableSelect from './EditableSelect';
import {
  readReserveRequestOptions,
  writeReserveRequestOptions,
} from './reserveRequestOptionsCache';
import {
  DIFF_FIELD_LABELS,
  VALUE_TYPE_DOLLAR,
  VALUE_TYPE_PERCENT,
  fmtBand,
  fmtCurrency,
  fmtReserveAmount,
  isPercentValueType,
  thresholdType,
} from './helpers';

/**
 * ReserveRequestQueue — cash reserves that still need creating in Tamarac.
 *
 * Requests reach the queue two ways: the CRM service-request workflow posts
 * them, and anyone can type one in here. Each open row is cross-referenced
 * against the reserves actually in Tamarac and labelled found / possible /
 * not found.
 *
 * That label is advisory and never acts on its own. A Tamarac reserve's id is a
 * hash of its whole definition, so a reserve entered even slightly differently
 * than requested can only be matched fuzzily — which is exactly why every row
 * stays until a person clears it, and why Dismiss is offered even when nothing
 * matched.
 */

// ─── verdict presentation ─────────────────────────────────────────────────────
const VERDICTS = {
  created: {
    icon: 'bi-check-circle-fill',
    className: 'text-success',
    label: 'Found in Tamarac',
  },
  possible: {
    icon: 'bi-exclamation-triangle-fill',
    className: 'text-warning',
    label: 'Possible match',
  },
  not_found: {
    icon: 'bi-hourglass-split',
    className: 'text-muted',
    label: 'Not found yet',
  },
};

const STATUS_LABELS = {
  created: 'Marked created',
  dismissed: 'Dismissed',
};

const describeDifferences = (differences = []) =>
  differences.map((field) => DIFF_FIELD_LABELS[field] || field).join(' and ');

const normalizeAccountNumber = (rawValue) => {
  const typed = (rawValue || '').trim();
  if (!typed) return '';
  const match = typed.match(/^([0-9][0-9-]*)\b/);
  return match ? match[1] : typed;
};

// ─── one queue row ────────────────────────────────────────────────────────────
const RequestRow = ({
  request,
  busy,
  confirmingDelete,
  onResolve,
  onReopen,
  onDelete,
  onAskDelete,
  onCancelDelete,
}) => {
  const isOpen = request.status === 'open';
  const verdict = VERDICTS[request.verdict] || VERDICTS.not_found;
  const match = request.match;
  const requestedBand = fmtBand(
    request.lower_threshold,
    request.lower_threshold_type,
    request.upper_threshold,
    request.upper_threshold_type
  );
  const foundBand = match
    ? fmtBand(
      match.lower_threshold,
      match.lower_threshold_type,
      match.upper_threshold,
      match.upper_threshold_type
    )
    : '';
  // Informational only — the reserve was still created, it just carries a
  // different tolerance than was asked for.
  const bandDiffers = (request.band_differences || []).length > 0;

  return (
    <tr className={isOpen ? undefined : 'text-muted'}>
      <td>
        <div className="fw-semibold">
          {request.account_name || <span className="fst-italic">Unknown account</span>}
        </div>
        <div className="small text-muted">
          {request.account_number}
          {/* A hand-typed number is the likeliest error here, and a number we
              have never seen is the only signal that it was mistyped. */}
          {request.account_known === false && (
            <span className="badge bg-danger-subtle text-danger-emphasis ms-2">
              <i className="bi bi-question-circle me-1"></i>
              Not a known account
            </span>
          )}
          {request.account_closed && (
            <span className="badge bg-secondary ms-2">Closed</span>
          )}
        </div>
      </td>
      <td>
        <div>{request.reserve_category || <span className="text-muted">—</span>}</div>
        {request.note && <div className="small text-muted">{request.note}</div>}
      </td>
      <td className="text-end">
        {fmtReserveAmount(request.reserve_amount, request.reserve_value_type)}
        {requestedBand && (
          <div className="small text-muted" title="Tolerance band around the target">
            {requestedBand}
          </div>
        )}
      </td>
      <td>
        <div className="small">
          <span className={`badge ${request.source === 'crm' ? 'bg-info' : 'bg-light text-dark border'}`}>
            {request.source === 'crm' ? 'CRM' : 'Manual'}
          </span>
        </div>
        <div className="small text-muted">
          {request.requested_at ? new Date(request.requested_at).toLocaleDateString() : '—'}
          {request.requested_by ? ` · ${request.requested_by}` : ''}
        </div>
      </td>
      <td>
        {isOpen ? (
          <>
            <span className={verdict.className}>
              <i className={`bi ${verdict.icon} me-1`}></i>
              {verdict.label}
            </span>
            {match && (
              <div className="small text-muted">
                {match.reserve_category || 'Uncategorized'} ·{' '}
                {/* Fall back to the calculated dollars when the reserve carries
                    no raw amount, so the line never degrades to a dash. */}
                {match.reserve_amount == null
                  ? fmtCurrency(match.calculated_reserve_dollar)
                  : fmtReserveAmount(match.reserve_amount, match.reserve_value_type)}
                {/* A rate means little without the money it comes to, so a
                    percent match shows both. */}
                {isPercentValueType(match.reserve_value_type) &&
                  match.calculated_reserve_dollar != null && (
                    <> ({fmtCurrency(match.calculated_reserve_dollar)})</>
                  )}
                {request.differences?.length > 0 && (
                  <> · differs by {describeDifferences(request.differences)}</>
                )}
              </div>
            )}
            {/* Deliberately its own line, and not folded into "differs by":
                that phrase means the verdict was downgraded, and a band
                mismatch never does that. */}
            {bandDiffers && (
              <div className="small text-warning-emphasis">
                <i className="bi bi-exclamation-triangle me-1"></i>
                Band differs: requested {requestedBand || 'none'}, found{' '}
                {foundBand || 'none'}
              </div>
            )}
          </>
        ) : (
          <>
            <span>{STATUS_LABELS[request.status] || request.status}</span>
            <div className="small text-muted">
              {request.resolved_by}
              {request.resolution_note ? ` · ${request.resolution_note}` : ''}
            </div>
          </>
        )}
      </td>
      <td className="text-end text-nowrap">
        {/* Deleting is the one action here with no Undo, so it takes a second
            click. Dismiss is the reversible way off the list; this is for a
            request that should never have been filed at all. */}
        {confirmingDelete ? (
          <>
            <span className="small text-danger me-2">Delete for good?</span>
            <button
              type="button"
              className="btn btn-sm btn-danger me-1"
              disabled={busy}
              onClick={() => onDelete(request.request_id)}
            >
              Delete
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={busy}
              onClick={onCancelDelete}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            {isOpen ? (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-success me-1"
                  disabled={busy}
                  onClick={() => onResolve(request.request_id, 'created')}
                  title="The reserve is in Tamarac — take this off the list"
                >
                  Mark created
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary me-1"
                  disabled={busy}
                  onClick={() => onResolve(request.request_id, 'dismissed')}
                  title="This reserve isn't needed, or was created in a form the match can't confirm"
                >
                  Dismiss
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary me-1"
                disabled={busy}
                onClick={() => onReopen(request.request_id)}
                title="Put this request back on the open list"
              >
                Undo
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              disabled={busy}
              onClick={() => onAskDelete(request.request_id)}
              title="Delete this request permanently — it will not appear under cleared requests"
              aria-label="Delete request"
            >
              <i className="bi bi-trash"></i>
            </button>
          </>
        )}
      </td>
    </tr>
  );
};

// ─── queue manager ────────────────────────────────────────────────────────────
const ReserveRequestQueue = ({ onClose, onChanged, initialRequests = null }) => {
  // Seeded from whatever the dashboard already loaded to badge its button, so
  // opening the modal paints immediately and revalidates behind that, instead
  // of spinning on data the page is already holding.
  const [requests, setRequests] = useState(initialRequests || []);
  // Suggestions for the add form's two editable dropdowns. Never a whitelist —
  // the inputs stay free text, so a genuinely new category can still be typed.
  const [options, setOptions] = useState(
    () => readReserveRequestOptions() || { accounts: [], categories: [] }
  );
  const [optionsLoading, setOptionsLoading] = useState(() => !readReserveRequestOptions());
  const [isLoading, setIsLoading] = useState(initialRequests === null);
  const [error, setError] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const [busyId, setBusyId] = useState('');
  // The row whose delete is awaiting confirmation. Only ever one at a time, so
  // a stray click can't leave several rows armed.
  const [confirmingDeleteId, setConfirmingDeleteId] = useState('');
  const [saving, setSaving] = useState(false);
  const [accountNumber, setAccountNumber] = useState('');
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [valueType, setValueType] = useState(VALUE_TYPE_DOLLAR);
  // The tolerance band. One number drives both sides unless split — 99% of real
  // reserves use the same figure each way, so symmetric is the honest default.
  const [band, setBand] = useState('');
  const [bandUpper, setBandUpper] = useState('');
  const [bandUnit, setBandUnit] = useState(VALUE_TYPE_PERCENT);
  const [splitBand, setSplitBand] = useState(false);
  const [note, setNote] = useState('');

  // `silent` skips the spinner. Swapping rows already on screen for a spinner
  // reads as a reload of something that was working — so it is only worth it
  // when there is nothing to show yet.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    setError('');
    try {
      const data = await cashReservesService.getReserveRequests();
      setRequests(data.requests || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load the reserve queue');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  // Captured once: whether the dashboard handed us rows to paint decides
  // whether this first fetch is a load or a revalidation.
  const seeded = useRef(initialRequests !== null);

  useEffect(() => {
    load({ silent: seeded.current });
  }, [load]);

  // Fetched separately from the queue: losing the suggestions degrades the two
  // dropdowns to plain text boxes, which is not worth failing the modal over.
  // Served from the module cache when it's warm, so reopening doesn't refetch
  // a list that changes about once a day.
  useEffect(() => {
    if (readReserveRequestOptions()) return undefined;
    let cancelled = false;
    setOptionsLoading(true);
    cashReservesService
      .getReserveRequestOptions()
      .then((data) => {
        const loaded = { accounts: data.accounts || [], categories: data.categories || [] };
        writeReserveRequestOptions(loaded);
        if (cancelled) return;
        setOptions(loaded);
      })
      .catch((err) => console.error('Could not load reserve request options:', err))
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openRequests = useMemo(
    () => requests.filter((request) => request.status === 'open'),
    [requests]
  );
  const rows = showResolved ? requests : openRequests;

  const isPercent = isPercentValueType(valueType);
  const bandIsPercent = isPercentValueType(bandUnit);
  const normalizedAccountNumber = useMemo(
    () => normalizeAccountNumber(accountNumber),
    [accountNumber]
  );

  // The band's effective two sides: one number fills both unless split.
  const lowerValue = band.trim();
  const upperValue = splitBand ? bandUpper.trim() : band.trim();

  // The server enforces the same rules; mirroring them here keeps the submit
  // button honest rather than letting a doomed request round-trip.
  // Rules that hold in EITHER unit. A number input already refuses most junk,
  // so the finite check is belt-and-braces — but negative is genuinely typeable
  // and used to reach the server only to bounce back as a 400.
  const amountNumber = Number(amount);
  const amountNotANumber = amount.trim() !== '' && !Number.isFinite(amountNumber);
  const amountNegative = amount.trim() !== '' && Number.isFinite(amountNumber) && amountNumber < 0;
  const percentOutOfRange =
    isPercent &&
    amount.trim() !== '' &&
    !amountNotANumber &&
    !amountNegative &&
    (amountNumber <= 0 || amountNumber > 100);
  // A band of 0 is legitimate ("no tolerance"), unlike a reserve of 0 — so the
  // upper bound is the only unit-specific rule. Being a finite, non-negative
  // number applies to dollars just as much as to percentages.
  //
  // Returns the reason rather than a boolean, so a disabled Add button can say
  // why — the same courtesy the amount field gets. Names the offending side
  // when the two are set separately.
  const bandProblem = (() => {
    const sides = splitBand
      ? [['Min', lowerValue], ['Max', upperValue]]
      : [['The band', lowerValue]];
    for (const [label, value] of sides) {
      if (value === '') continue;
      const n = Number(value);
      if (!Number.isFinite(n)) return `${label} isn't a number — enter digits only.`;
      if (n < 0) return `${label} can't be negative.`;
      if (bandIsPercent && n > 100) {
        return `${label} must be at most 100% — switch the unit to $ for a dollar band.`;
      }
    }
    return null;
  })();
  const bandOutOfRange = bandProblem !== null;
  const canSubmit =
    normalizedAccountNumber !== '' &&
    (category.trim() !== '' || amount.trim() !== '') &&
    !amountNotANumber &&
    !amountNegative &&
    !percentOutOfRange &&
    !bandOutOfRange;

  const accountOptions = useMemo(
    () =>
      options.accounts.map((account) => ({
        value: account.account_number,
        label: account.account_name,
      })),
    [options.accounts]
  );
  const formatAccountOption = useCallback(
    (option) => (option.label ? `${option.value} ${option.label}` : option.value),
    []
  );
  const categoryOptions = useMemo(
    () => options.categories.map((name) => ({ value: name, label: '' })),
    [options.categories]
  );

  // Naming the account as it's typed is the cheapest possible check on the
  // number, and catches a transposed digit before the request is ever filed.
  const typedAccount = useMemo(() => {
    const typed = normalizedAccountNumber;
    if (!typed) return null;
    return options.accounts.find((account) => account.account_number === typed) || null;
  }, [normalizedAccountNumber, options.accounts]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      await cashReservesService.addReserveRequest({
        account_number: normalizedAccountNumber,
        reserve_category: category.trim(),
        // Blank stays blank rather than becoming 0, which would match nothing
        // and read as a real requested amount.
        reserve_amount: amount.trim() === '' ? null : Number(amount),
        reserve_value_type: valueType,
        // Each side carries its own Tamarac spelling — "% of the reserve"
        // below, "% above the reserve" above. A side left blank sends no unit
        // either, so an unspecified band doesn't look configured.
        lower_threshold: lowerValue === '' ? null : Number(lowerValue),
        lower_threshold_type: lowerValue === '' ? null : thresholdType(bandIsPercent, false),
        upper_threshold: upperValue === '' ? null : Number(upperValue),
        upper_threshold_type: upperValue === '' ? null : thresholdType(bandIsPercent, true),
        note: note.trim(),
      });
      setAccountNumber('');
      setCategory('');
      setAmount('');
      setBand('');
      setBandUpper('');
      // The unit is deliberately NOT reset: someone queueing management fees
      // enters a run of percentages, and re-picking % each time is friction.
      setNote('');
      await load({ silent: true });
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to add the request');
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (requestId, action, failureMessage) => {
    setBusyId(requestId);
    setError('');
    try {
      await action();
      await load({ silent: true });
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || failureMessage);
    } finally {
      setBusyId('');
    }
  };

  const handleResolve = (requestId, resolution) =>
    runAction(
      requestId,
      () => cashReservesService.resolveReserveRequest(requestId, { resolution }),
      'Failed to resolve the request'
    );

  const handleReopen = (requestId) =>
    runAction(
      requestId,
      () => cashReservesService.reopenReserveRequest(requestId),
      'Failed to reopen the request'
    );

  const handleDelete = async (requestId) => {
    await runAction(
      requestId,
      () => cashReservesService.deleteReserveRequest(requestId),
      'Failed to delete the request'
    );
    // Disarmed either way: on success the row is gone, and on failure leaving
    // it armed would invite a second click at an error nobody has read yet.
    setConfirmingDeleteId('');
  };

  return (
    <div className="cash-reserve-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="card shadow"
        style={{ maxWidth: '1400px', width: '100%', maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-header d-flex justify-content-between align-items-center">
          <span className="fw-semibold">
            <i className="bi bi-clipboard-plus me-2 text-primary"></i>
            Reserve Creation Queue
            {openRequests.length > 0 && (
              <span className="badge bg-primary ms-2">{openRequests.length}</span>
            )}
          </span>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
        </div>
        {/* minHeight: 0 lets the body shrink below its content (a flex child's
            default min-height is auto), so the card's 85vh cap actually holds
            and the body scrolls instead of spilling out of the modal. */}
        <div className="card-body" style={{ overflowY: 'auto', minHeight: 0 }}>
          <p className="text-muted small">
            Cash reserves that have been requested — by the CRM service-request workflow, or added
            here by hand — checked against what is actually in Tamarac. Nothing is removed
            automatically: a reserve entered differently than requested won&apos;t match exactly, so
            clearing a row is always your call.
          </p>

          {error && (
            <div className="alert alert-danger py-2 small" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </div>
          )}

          <form className="row g-2 align-items-end mb-3" onSubmit={handleAdd}>
            <div className="col-sm-3">
              <label className="form-label small mb-1" htmlFor="reserveRequestAccount">
                Account #
              </label>
              <EditableSelect
                id="reserveRequestAccount"
                placeholder="e.g. 08001234"
                value={accountNumber}
                onChange={setAccountNumber}
                options={accountOptions}
                formatSelectedValue={formatAccountOption}
                loading={optionsLoading}
                loadingMessage="Loading accounts…"
                emptyMessage="Account list unavailable — type the number"
                optionsLabel="Show account options"
              />
            </div>
            <div className="col-sm-3">
              <label className="form-label small mb-1" htmlFor="reserveRequestCategory">
                Category
              </label>
              <EditableSelect
                id="reserveRequestCategory"
                placeholder="e.g. RMD"
                value={category}
                onChange={setCategory}
                options={categoryOptions}
                loading={optionsLoading}
                loadingMessage="Loading categories…"
                emptyMessage="Category list unavailable — type the category"
                optionsLabel="Show category options"
              />
            </div>
            <div className="col-sm-6">
              <label className="form-label small mb-1" htmlFor="reserveRequestNote">
                Note (optional)
              </label>
              <input
                id="reserveRequestNote"
                type="text"
                className="form-control form-control-sm"
                placeholder="e.g. Client called 7/29"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            {/* Second row: the amount, the tolerance band it is measured
                against, and the submit alongside them. */}
            <div className="col-sm-3">
              <label className="form-label small mb-1" htmlFor="reserveRequestAmount">
                Amount
              </label>
              {/* Amount and unit together: most Tamarac reserves are a
                  percent of account value, not a dollar figure, so the unit is
                  part of the amount rather than an afterthought. */}
              <div className="input-group input-group-sm">
                <input
                  id="reserveRequestAmount"
                  type="number"
                  step={isPercent ? '0.01' : '1'}
                  min="0"
                  max={isPercent ? '100' : undefined}
                  className="form-control form-control-sm"
                  // The unit rides in the placeholder too: a bare "0.75" reads
                  // just as easily as a multiplier as it does a rate.
                  placeholder={isPercent ? 'e.g. 0.75%' : 'e.g. 24000'}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <select
                  className="form-select form-select-sm flex-grow-0 w-auto"
                  aria-label="Amount unit"
                  value={valueType}
                  onChange={(e) => setValueType(e.target.value)}
                >
                  <option value={VALUE_TYPE_DOLLAR}>$</option>
                  <option value={VALUE_TYPE_PERCENT}>%</option>
                </select>
              </div>
            </div>
            <div className="col-sm-3">
              <label className="form-label small mb-1" htmlFor="reserveRequestBand">
                {splitBand ? 'Min (below target)' : 'Band around target'}
              </label>
              <div className="input-group input-group-sm">
                {!splitBand && <span className="input-group-text">±</span>}
                {splitBand && <span className="input-group-text">−</span>}
                <input
                  id="reserveRequestBand"
                  type="number"
                  step="0.01"
                  min="0"
                  max={bandIsPercent ? '100' : undefined}
                  className="form-control form-control-sm"
                  placeholder={bandIsPercent ? 'e.g. 33%' : 'e.g. 5000'}
                  value={band}
                  onChange={(e) => setBand(e.target.value)}
                />
                <select
                  className="form-select form-select-sm flex-grow-0 w-auto"
                  aria-label="Band unit"
                  value={bandUnit}
                  onChange={(e) => setBandUnit(e.target.value)}
                >
                  <option value={VALUE_TYPE_DOLLAR}>$</option>
                  <option value={VALUE_TYPE_PERCENT}>%</option>
                </select>
              </div>
            </div>

            {splitBand && (
              <div className="col-sm-2">
                <label className="form-label small mb-1" htmlFor="reserveRequestBandUpper">
                  Max (above target)
                </label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text">+</span>
                  <input
                    id="reserveRequestBandUpper"
                    type="number"
                    step="0.01"
                    min="0"
                    max={bandIsPercent ? '100' : undefined}
                    className="form-control form-control-sm"
                    placeholder={bandIsPercent ? 'e.g. 33%' : 'e.g. 5000'}
                    value={bandUpper}
                    onChange={(e) => setBandUpper(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className={splitBand ? 'col-sm-2' : 'col-sm-4'}>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none"
                onClick={() => {
                  // Splitting seeds the upper side from the band already typed,
                  // so the common symmetric value isn't lost on the way in.
                  if (!splitBand && bandUpper.trim() === '') setBandUpper(band);
                  setSplitBand((v) => !v);
                }}
              >
                {splitBand ? 'Use one band for both' : 'Set min and max separately'}
              </button>
            </div>

            <div className="col-sm-2 d-grid ms-auto">
              <button
                type="submit"
                className="btn btn-sm btn-primary"
                disabled={saving || !canSubmit}
                title="A category, an amount, or both — with neither there is nothing to match on"
              >
                <i className={`bi ${saving ? 'bi-hourglass-split' : 'bi-plus-lg'} me-1`}></i>
                {saving ? 'Adding…' : 'Add'}
              </button>
            </div>
          </form>

          {/* Restating the amount in words is the whole defence against the
              0.75 ambiguity: a rate and a multiplier look identical typed, but
              "0.75% of account value" and "75% of account value" do not. It
              also gives the out-of-range case somewhere to explain itself,
              rather than just disabling Add with no reason given. */}
          {amount.trim() !== '' && (
            <div className="small mb-2">
              {amountNotANumber ? (
                <span className="text-danger">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  That isn&apos;t a number — enter digits only, with no commas or
                  currency symbol.
                </span>
              ) : amountNegative ? (
                <span className="text-danger">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  An amount can&apos;t be negative.
                </span>
              ) : percentOutOfRange ? (
                <span className="text-danger">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  A percentage must be over 0 and at most 100. Did you mean{' '}
                  {fmtCurrency(Number(amount))} — switch the unit to $?
                </span>
              ) : (
                <span className="text-muted">
                  Requesting{' '}
                  <span className="fw-semibold">
                    {fmtReserveAmount(Number(amount), valueType)}
                  </span>
                  {isPercent && ' of account value'}
                </span>
              )}
            </div>
          )}

          {/* The band gets the same treatment as the amount: a disabled Add
              button should never leave you guessing which field it objects to. */}
          {bandProblem && (
            <div className="small mb-2">
              <span className="text-danger">
                <i className="bi bi-exclamation-triangle me-1"></i>
                {bandProblem}
              </span>
            </div>
          )}

          {/* A known number needs no confirmation line: the field itself then
              reads "number name". Only an unknown one is called out. */}
          {accountNumber.trim() !== '' && options.accounts.length > 0 && !typedAccount && (
            <div className="small mb-2">
              <span className="text-warning-emphasis">
                <i className="bi bi-exclamation-triangle me-1"></i>
                Not a known account number — check it, or carry on if the account is new.
              </span>
            </div>
          )}

          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="showResolvedRequests"
              checked={showResolved}
              onChange={(e) => {
                // An armed row scrolling out of view and back would otherwise
                // still be one click from deletion.
                setConfirmingDeleteId('');
                setShowResolved(e.target.checked);
              }}
            />
            <label className="form-check-label small" htmlFor="showResolvedRequests">
              Show cleared requests
            </label>
          </div>

          {isLoading ? (
            <div className="text-center py-3 text-muted">
              <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-3 text-muted">
              <i className="bi bi-inbox me-2"></i>
              {requests.length === 0
                ? 'No reserves are waiting to be created.'
                : 'Nothing is outstanding — every request has been cleared.'}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Account</th>
                    <th>Category</th>
                    <th className="text-end">Amount</th>
                    <th>Requested</th>
                    <th>In Tamarac?</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((request) => (
                    <RequestRow
                      key={request.request_id}
                      request={request}
                      busy={busyId === request.request_id}
                      confirmingDelete={confirmingDeleteId === request.request_id}
                      onResolve={handleResolve}
                      onReopen={handleReopen}
                      onDelete={handleDelete}
                      onAskDelete={setConfirmingDeleteId}
                      onCancelDelete={() => setConfirmingDeleteId('')}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReserveRequestQueue;
